<template>
  <div id="app" class="flex flex-col w-full min-h-screen overflow-x-hidden | body-bg">
    <transition name="fade">
      <nav
        class="w-full flex items-center fixed top-0 z-40 h-14 border-b border-transparent transition-250 transition-all"
        v-if="$auth.ready()"
        :class="{'bg-grey-2': scrolled, 'border-grey-7': (scrolled && breadcrumbs), 'border-yellow-7': scrolled}"
        style="animation-duration: 1s"
      >
        <div class="flex justify-between items-center w-full px-2 mx-auto | container">
          <div class="text-white font-serif flex items-center">
            <router-link :to="{name: 'channels.index'}" class="text-2xl text-blue-8">
              <img src="@/assets/logo.png" class="w-auto h-8" />
            </router-link>
            <router-link
              class="text-yellow-7 hover:text-grey-8 ml-2 transition-250 transition-all"
              v-if="$auth.check('admin') || $auth.check('super-moderator')"
              :to="{name: 'admin'}"
            >
              <sliders-icon></sliders-icon>
            </router-link>
            <router-link
              class="text-yellow-7 hover:text-grey-8 ml-2 transition-250 transition-all"
              :to="{name: 'users.index'}"
            >
              <users-icon></users-icon>
            </router-link>
          </div>
          <auth></auth>
        </div>
      </nav>
    </transition>

    <main class="flex items-center flex-col flex-1 min-h-screen">
      <div class="w-full" v-if="$auth.ready()">
        <portal-target
          name="breadcrumb-portal"
          slim
          :transition="{name: 'fade', mode: 'out-in'}"
          class="w-full absolute top-0 z-30 mt-14 h-6 text-sm"
          :class="{'border-grey-7': !scrolled}"
          ref="breadcrumbPortal"
        ></portal-target>

        <portal-target
          name="banner-portal"
          slim
          :transition="{name: 'fade', mode: 'out-in'}"
          class="flex justify-center items-center bg-grey-2 w-screen"
          style="height: 250px"
        ></portal-target>

        <div class="flex justify-center items-center w-full mt-4 mb-8 mx-auto | container">
          <div class="w-full">
            <transition name="fade" mode="out-in">
              <router-view></router-view>
            </transition>
          </div>
        </div>
      </div>
    </main>

    <transition name="fade">
      <footer class="w-full" :class="{'mb-8': $auth.check() && !$auth.user().verified}" v-if="$auth.ready()" style="animation-duration: 2s">
        <div class="w-full | footer-bg">
          <div class="w-full flex flex-wrap mx-auto | container">
            <div
              class="w-full lg:w-3/5 flex flex-wrap px-4 py-2 border-b-2 lg:border-r-2 lg:border-b-0 border-blue-5 | geoff-bg"
            >
              <div
                class="w-full sm:w-3/5 lg:w-1/2 text-center sm:text-left order-last sm:order-first"
              >
                <h2
                  class="text-3xl text-yellow-6 tracking-wider font-bold text-blue-1"
                >En Taro iNcontroL</h2>
                <p class="text-xl text-blue-7 semi-bold">Sept. 11, 1985 - July 20, 2019</p>
                <p
                  class="text-lg text-blue-8 pb-2"
                >Dedicated to the eternal memory of Geoffrey Robinson</p>
                <p
                  class="text-base text-blue-9 pb-2"
                >A pillar of the community, a friend to us all and a founder of The Pylon Show taken from this world far too soon.</p>
                <p class="text-base text-blue-9 pb-2">Long Live iNcontroL.</p>
              </div>
              <div
                class="order-first sm:order-last w-full sm:w-2/5 flex items-center justify-center md:hidden"
              >
                <img
                  src="@/assets/geoff.png"
                  class="w-32 h-32 sm:w-48 sm:h-48 object-cover rounded-full border-2 border-yellow-7"
                />
              </div>
            </div>
            <div class="w-full lg:w-2/5 py-2">
              <ul
                class="w-full h-full text-center flex flex-col justify-around uppercase tracking-wide text-grey-8 | footer-links"
              >
                <li class="py-2">
                  <a href="https://www.thepylonshow.com">The Pylon Show</a>
                </li>
                <li class="py-2">
                  <router-link :to="{name: 'channels.index'}">Channels</router-link>
                </li>
                <li class="py-2">
                  <router-link :to="{name: 'users.index'}">Users</router-link>
                </li>
                <li class="py-2">
                  <router-link :to="{name: 'privacy'}">Privacy Policy</router-link>
                </li>
                <li class="py-2">
                  <router-link :to="{name: 'terms'}">Terms of Use</router-link>
                </li>
                <li
                  class="w-full max-w-sm sm:max-w-sm mx-auto flex justify-around py-2 | footer-social"
                >
                  <a
                    href="https://www.patreon.com/ThePylonShow/"
                    target="_blank"
                    class="text-blue-2"
                  >
                    <svgicon icon="patreon"></svgicon>
                  </a>
                  <a href="https://twitter.com/ThePylonShow" target="_blank" class="text-blue-2">
                    <svgicon icon="twitter"></svgicon>
                  </a>
                  <a href="https://www.twitch.tv/artosis" target="_blank" class="text-blue-2">
                    <svgicon icon="twitch"></svgicon>
                  </a>
                  <a href="https://www.youtube.com/artosistv" target="_blank" class="text-blue-2">
                    <svgicon icon="youtube"></svgicon>
                  </a>
                  <a
                    href="https://www.instagram.com/thepylonshow/"
                    target="_blank"
                    class="text-blue-2"
                  >
                    <svgicon icon="instagram"></svgicon>
                  </a>
                  <a href="https://discord.gg/ga5umfc" target="_blank" class="text-blue-2">
                    <svgicon icon="discord"></svgicon>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="text-grey-7 text-center text-sm py-2 px-4 | footer-copyright">
          Copyright 2018-{{ currentYear }} The Pylon Show. All Rights Reserved.
          <br />StarCraft, StarCraft 2 and other related entities are property of Blizzard Entertainment.
          <br />
          <div class="flex items-baseline justify-center">
            Made with
            <img class="w-auto h-4 mx-1" src="@/assets/love.png" /> using
            <a
              href="https://laravel.com/"
              class="text-grey-8 hover:text-blue-8 ml-1"
              target="_blank"
            >Laravel</a>,
            <a
              href="https://vuejs.org/"
              class="text-grey-8 hover:text-blue-8 ml-1"
              target="_blank"
            >Vue</a>,
            <a
              href="https://tailwindcss.com/"
              class="text-grey-8 hover:text-blue-8 ml-1"
              target="_blank"
            >Tailwind</a>&nbsp;&
            <a
              @click="show.credits = true"
              class="text-grey-8 hover:text-blue-8 ml-1 cursor-pointer"
              target="_blank"
            >more</a>.
          </div>
        </div>
      </footer>
    </transition>

    <div
      class="w-full bg-blue-0 text-red-8 border-t-2 border-red-0 fixed bottom-0 z-30 mt-14 py-2 flex items-center justify-center text-sm px-2"
       v-if="$auth.ready() && $auth.check() && !$auth.user().verified"
    >
      You must verify your email before you are able to participate. Please check your email to continue.
      <a
        class="cursor-pointer underline text-red-7 hover:text-red-6 pl-1"
        @click="resendEmail"
      >Resend Verification</a>.
    </div>

    <cookie @clicked-decline="cookieClickedDecline"></cookie>

    <portal-target name="modal-portal" slim :transition="{name: 'fade'}"></portal-target>

    <portal to="modal-portal">
      <credits-modal v-if="show.credits" @close="show.credits = false"></credits-modal>
    </portal>
  </div>
</template>

<script>
import Cookie from "@/components/Cookie"
import CreditsModal from "@/components/Modals/CreditsModal"
import moment from "moment"
import { mapGetters } from "vuex"
import { UsersIcon, SlidersIcon } from "vue-feather-icons"

export default {
  name: "App",
  components: {
    Cookie,
    CreditsModal,
    UsersIcon,
    SlidersIcon
  },
  data() {
    return {
      scrolled: false,
      show: {
        credits: false
      }
    }
  },
  refs: ["breadcrumbPortal"],
  computed: {
    ...mapGetters({
      emotes: "emotes/list"
    }),
    currentYear() {
      return moment().format("Y")
    },
    breadcrumbs() {
      if (!this.$refs.breadcrumbPortal) return false
      let el = this.$refs.breadcrumbPortal.$el
      return el instanceof Element || el instanceof HTMLDocument
    }
  },
  watch: {
    emotes() {
      this.genereateEmoteStyles()
    }
  },
  methods: {
    genereateEmoteStyles() {
      let head = document.getElementsByTagName("head")[0],
        s = document.createElement("style"),
        existing = document.querySelector("[data-emote-styles]")

      s.setAttribute("type", "text/css")
      s.setAttribute("data-emote-styles", "")

      let css = ""

      this.emotes.forEach(function(item) {
        css += `span.emote[data-emote="${item.name}"]{background:url("${item.url}") no-repeat;}`
      })

      if (existing) {
        existing.parentNode.removeChild(existing)
      }

      if (s.styleSheet) {
        s.styleSheet.cssText = css
      } else {
        s.appendChild(document.createTextNode(css))
      }
      head.appendChild(s)
    },
    handleScroll() {
      this.scrolled = window.scrollY > 0
    },
    cookieClickedDecline() {
      this.$ga.disable()
    },
    async resendEmail() {
      await this.$api.auth
        .resendVerificationEmail()
        .then(() => {
          this.$toasted.success(
            "Verficiation email resent. Please check the spam folder if it is not in the inbox."
          )
        })
        .catch(data => {
          if (data.response && data.response.status === 422) {
            this.$toasted.info("Your account has already been verified.")
          } else {
            this.$error.generic()
          }
        })
    }
  },
  mounted() {
    window.addEventListener("scroll", this.handleScroll)
    this.handleScroll()
  },
  destroyed() {
    window.removeEventListener("scroll", this.handleScroll)
  }
}
</script>
